import styled from 'styled-components';

export const AnalyticsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  width: 100%;
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
`;

export const PageTitle = styled.h1`
  font-size: 2.1rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  margin-bottom: 0.3rem;
  text-align: left;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: var(--mobile-headline, 1.5rem);
  }
`;

export const PageDescription = styled.p`
  font-size: 1rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 0.5rem;
  text-align: left;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: var(--mobile-paragraph, 0.75rem);
  }
`;

export const DataSelectionSection = styled.section`
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
  padding: 0.85rem;
  margin-bottom: 1rem;
  border: 1px solid var(--card-border);
  box-shadow: var(--shadow-sm);
  
  @media (max-width: 768px) {
    padding: 0.65rem;
  }
`;

export const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.6rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

export const SelectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0; /* Prevents grid overflow */
`;

export const SelectionLabel = styled.label`
  font-size: 0.65rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  margin-bottom: 0.2rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

export const SelectionInput = styled.input`
  padding: 0.35rem 0.5rem;
  font-size: 0.65rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--card-border);
  font-family: 'Poppins', sans-serif;
  background: var(--bg-primary);
  color: var(--text-dark);
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 2px rgba(255, 150, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.3rem 0.45rem;
    font-size: 0.6rem;
  }
`;

export const SelectionSelect = styled.select`
  padding: 0.35rem 0.5rem;
  font-size: 0.65rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--card-border);
  font-family: 'Poppins', sans-serif;
  background: var(--bg-primary);
  color: var(--text-dark);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 2px rgba(255, 150, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.3rem 0.45rem;
    font-size: 0.6rem;
  }
`;

export const MultiSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 160px;
  overflow-y: auto;
  padding: 0.4rem;
  background: var(--bg-primary);
  border-radius: var(--radius-sm);
  border: 1px solid var(--card-border);
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--bg-secondary);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--card-border);
    border-radius: 3px;
    
    &:hover {
      background: var(--accent-orange);
    }
  }
  
  @media (max-width: 768px) {
    max-height: 140px;
    padding: 0.35rem;
  }
`;

export const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.65rem;
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  padding: 0.15rem 0;
  transition: color 0.2s;

  &:hover {
    color: var(--accent-orange);
  }

  input[type="checkbox"] {
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: var(--accent-orange);
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

export const ComparisonToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--card-border);
  
  @media (max-width: 768px) {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
  }
`;

export const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.65rem;
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  transition: color 0.2s;

  &:hover {
    color: var(--accent-orange);
  }

  input[type="checkbox"] {
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: var(--accent-orange);
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

export const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

export const DateInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  
  label {
    font-size: 0.65rem;
    color: var(--text-gray);
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
  }
`;

/* Graph design matched to /coach/home Average Rating graph */
export const GraphSection = styled.section`
  width: 100%;
  max-width: 1400px;
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem 1.5rem 1.5rem 0.5rem;
  margin-bottom: 3rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    margin-left: -1.25rem;
    width: calc(100% + 2.5rem);
    padding: 1rem 1rem 1rem 0;
    border-radius: 0;
  }
`;

export const GraphTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--text-gray);
  margin-bottom: 0.75rem;
  text-align: center;
  font-family: 'Poppins', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

export const GraphContainer = styled.div`
  width: 100%;
  height: 380px;
  .recharts-cartesian-grid-horizontal line,
  .recharts-cartesian-grid-vertical line {
    stroke: rgba(0,0,0,0.06);
  }
  .recharts-text {
    font-size: 0.7rem;
    fill: var(--text-gray);
  }
  .recharts-tooltip-wrapper {
    outline: none;
  }
  .recharts-default-tooltip {
    border-radius: 8px !important;
    border: none !important;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08) !important;
    font-size: 0.8rem !important;
  }
  .recharts-legend-item-text {
    font-size: 0.65rem;
    font-weight: 600;
  }
  @media (max-width: 768px) {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    .recharts-legend-wrapper {
      margin-top: 0.9rem;
      padding-top: 0.25rem;
    }
    .recharts-legend-item {
      margin-bottom: 0.15rem;
      margin-top: 0;
    }
    > div {
      margin-left: 0 !important;
      padding-left: 0 !important;
    }
    svg {
      overflow: visible;
      width: 100% !important;
    }
    .recharts-cartesian-axis-tick {
      font-size: 0.65rem !important;
    }
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-gray);
  opacity: 0.6;
  font-size: 1rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
`;

export const NoDataMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem;
  min-height: 400px;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  font-style: normal;
  opacity: 0.7;
`;

export const UpdateButton = styled.button`
  padding: var(--button-padding, 0.4rem 0.75rem);
  border-radius: var(--radius-sm);
  border: none;
  background: var(--accent-orange);
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: var(--button-font-size, 0.6rem);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  outline: none;
  white-space: nowrap;
  margin-top: 0.5rem;
  width: 100%;

  &:hover, &:focus {
    background: var(--accent-orange-dark);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(255, 150, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(255, 150, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 0.35rem 0.6rem;
    font-size: var(--button-font-size, 0.6rem);
  }
`;

export const ShowDataButton = styled.button`
  padding: var(--button-padding, 0.4rem 0.75rem);
  border-radius: var(--radius-sm);
  border: none;
  background: var(--accent-orange);
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: var(--button-font-size, 0.6rem);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  outline: none;
  white-space: nowrap;
  position: absolute;
  bottom: 1rem;
  right: 1rem;

  &:hover, &:focus {
    background: var(--accent-orange-dark);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(0);
    box-shadow: var(--shadow-sm);
  }

  &:disabled {
    background: var(--accent-orange);
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const QuickDateButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 0.3rem;
  }
`;

export const QuickDateButton = styled.button`
  padding: 0.3rem 0.5rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--card-border);
  background: var(--bg-primary);
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
  font-size: 0.6rem;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
  white-space: nowrap;
  flex: 1;
  min-width: fit-content;
  text-transform: uppercase;
  letter-spacing: 0.02em;

  &:hover, &:focus {
    background: var(--accent-orange);
    border-color: var(--accent-orange);
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(255, 150, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 0.25rem 0.4rem;
    font-size: 0.55rem;
    flex: 1 1 calc(50% - 0.15rem);
  }
`;

export const DateRangeWithButton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  width: 100%;
`;

export const FilterSectionTitle = styled.h3`
  font-size: 0.75rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-bottom: 0.4rem;
  }
`;

// Period selector positioned in top-right corner
export const PeriodSelectorTopRight = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
  position: absolute;
  top: 1rem;
  right: 1rem;
  
  @media (max-width: 768px) {
    position: static;
    justify-content: center;
    margin-top: 0.5rem;
    margin-bottom: 0.75rem;
    gap: 0.4rem;
  }
`;

export const PeriodButtonWrapperSmall = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  width: 50px;
  
  @media (max-width: 768px) {
    width: 45px;
    gap: 0.2rem;
  }
`;

export const PeriodButtonSmall = styled.button<{ $isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: none;
  font-family: 'Poppins', sans-serif;
  font-size: 0.7rem;
  font-weight: 900;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => props.$isSelected 
    ? `
      background: var(--accent-orange);
      color: #fff;
      box-shadow: 0 2px 4px rgba(255, 150, 0, 0.3);
    `
    : `
      background: #fff;
      color: var(--accent-orange);
      border: 1.5px solid var(--accent-orange);
      
      &:hover {
        background: rgba(255, 150, 0, 0.1);
      }
    `
  }
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
    font-size: 0.65rem;
  }
`;

export const PeriodLabelSmall = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 0.6rem;
  font-weight: 600;
  color: var(--text-gray);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.55rem;
  }
`;

export const GraphSectionWithPeriodSelector = styled(GraphSection)`
  position: relative;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

